<template>
  <section class="booking-appointment-statistics">
    <Breadcrumbs
      :breadcrumbs="[
        { title: $t('menu.statistics'), disabled: true },
        { title: $t('menu.agenda/statistics'), disabled: true }
      ]">
    </Breadcrumbs>
    <!-- index page filters -->

    <section class="booking-appointment-statistics-filters">

      <!-- Selected Entities Filters -->
      <form class="evollu-form row mb-3">

        <div class="col-12 col-md-4">
          <label class="form-label">{{$t('tables.filter')}}</label>
          <va-input
            v-model="filters.query"
            :placeholder="$t('agenda.statistics.search_promoter')"
          />
        </div>

        <div class="col-12 col-md-4">
          <label class="form-label">{{$t('agenda.screenings.labels.entities')}}:</label>
          <multiselect
            v-model="filters.entities"
            :options="entityOptions"
            track-by="uid"
            label="name"
            :custom-label="formatedEntityLabel"
            select-label=""
            selected-label=""
            deselect-label="×"
            :placeholder="$t('agenda.screenings.labels.all_entities')"
            :allow-empty="true"
            :multiple="true"
            :searchable="true"
            :limit="10"
            :limit-text="limitText">
            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="filters.entities.length" @mousedown.prevent.stop="filters.entities = []"></div>
            </template>
            <span slot="noResult">{{$t('patients.noResults')}}</span>
          </multiselect>
        </div>

        <!-- Selected Times Filter -->
        <div class="col-12 col-md-4">
          <RangeDatePicker
            id="range-date-picker"
            :label="$t('agenda.statistics.created_date')"
            v-model="filters.dateInterval"
            placeholder="---"
            :clearable="false"
          >
          </RangeDatePicker>
        </div>

      </form>
      <p v-if="activeEntitiesFilters >= 0">{{$t('agenda.statistics.number_results', {number: activeEntitiesFilters})}}</p>
    </section>

    <fade-transition mode="out-in" :duration="500">
      <div key="1" v-if="isLoading">
        <span class="d-flex justify-content-center mt-2"><b>{{$t('agenda.statistics.loading')}}</b></span>
      </div>
      <div key="2" v-else>
        <div key="metrics" v-if="hasInfoToDisplay">
          <!-- General statistics -->
          <dashboard-info-widgets
            v-bind="propsDashboard"
            class="mt-1"
          ></dashboard-info-widgets>

          <br>

          <!-- Graph statistics -->
          <ranking-graph
            v-for="graph in graphTypes"
            :key="graph.id"
            :graph="graph"
          ></ranking-graph>
        </div>
        <div key="no-info" class="py-4" v-else>
          <span class="d-flex justify-content-center py-1 mt-2"><b>{{$t('agenda.statistics.no_info')}}</b></span>
        </div>
      </div>
    </fade-transition>
  </section>
</template>
<script>
import { debounce } from 'lodash'
import { mapGetters } from 'vuex'
import Multiselect from 'vue-multiselect'
import * as criterias from './utils/criterias'
import RankingGraph from './components/RankingGraph'
import { StatisticsService } from '@/api/BookingService'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import DashboardInfoWidgets from './components/DashboardInfoWidgets'
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import { FILTERS_AGENDA_STATISTICS } from '@/local-storage/LocalStorageKeys'

export default {
  props: {
    component: {
      type: Object,
      required: true
    }
  },
  components: {
    Multiselect,
    Breadcrumbs,
    RankingGraph,
    FadeTransition,
    RangeDatePicker,
    DashboardInfoWidgets
  },
  mixins: [
    ClustersAndEntitiesMixin,
    MultiselectLabelFormatter
  ],
  created: function () {
    const state = LocalStorageHelper.get(FILTERS_AGENDA_STATISTICS)
    this.loadLocalStorage(state).then(() => {
      this.isLoading = false
      this.loadStats()
    })
  },
  data: function () {
    return {
      stats: {},
      isLoading: true,
      propsDashboard: {
        appointments: 0,
        contacts: 0,
        contactsWithLoss: 0,
        leads: 0,
        screenings: 0,
        promoters: 0
      },
      filters: {
        dateInterval: {
          start: this.moment().format('YYYY-MM-DD'),
          end: this.moment().add(-1, 'M').format('YYYY-MM-DD')
        },
        entities: [],
        query: ''
      },
      searchInput: '',
      selectedEntityIds: [],
      selectedEntityUids: [],
      pallete: ['#266F62', '#279381', '#28B199', '#29C4A9', '#68D5C2'],
    }
  },
  computed: {
    ...mapGetters([
      'clusters'
    ]),
    hasInfoToDisplay: function () {
      return this.stats.num_screenings
    },
    graphTypes: function () {
      return [
        {
          id: 1,
          name: this.$t('agenda.statistics.graphs.per_facilities.title'),
          labels: [
            { key: 'contacts', text: this.$t('agenda.statistics.graphs.per_facilities.labels.contacts'), color: this.pallete[0], hidden: true },
            { key: 'contactsWithLoss', text: this.$t('agenda.statistics.graphs.per_facilities.labels.contactsWithLoss'), color: this.pallete[1], hidden: true },
            { key: 'leads', text: this.$t('agenda.statistics.graphs.per_facilities.labels.leads'), color: this.pallete[2], hidden: true },
            { key: 'screenings', text: this.$t('agenda.statistics.graphs.per_facilities.labels.screenings'), color: this.pallete[3] },
            { key: 'appointments', text: this.$t('agenda.statistics.graphs.per_facilities.labels.appointments'), color: this.pallete[4] }
          ],
          dataset: this.filteredFacilities || [],
          dataset_key: 'facility_name',
          criterias: [
            { id: 1, name: this.$t('agenda.statistics.criteria.by_contacts'), criteria: criterias.criteriaByContacts },
            { id: 2, name: this.$t('agenda.statistics.criteria.by_contacts_with_loss'), criteria: criterias.criteriaByContactsWithLoss },
            { id: 3, name: this.$t('agenda.statistics.criteria.by_lqe'), criteria: criterias.criteriaByLeads },
            { id: 4, name: this.$t('agenda.statistics.criteria.by_screenings'), criteria: criterias.criteriaByScreenings },
            { id: 5, name: this.$t('agenda.statistics.criteria.by_appointments'), criteria: criterias.criteriaByAppointments },
            { id: 6, name: this.$t('agenda.statistics.criteria.by_contacts_per_screening'), criteria: criterias.criteriaByAverageContactsPerScreening },
            { id: 7, name: this.$t('agenda.statistics.criteria.by_contacts_with_loss_per_screening'), criteria: criterias.criteriaByAverageContactsWithLossPerScreening },
            { id: 8, name: this.$t('agenda.statistics.criteria.by_lqe_per_screening'), criteria: criterias.criteriaByAverageLeadsPerScreening },
          ],
          localStorageKey: 'STATS_TOP_FACILITIES',
        },
        {
          id: 2,
          name: this.$t('agenda.statistics.graphs.per_promoters.title'),
          labels: [
            { key: 'contacts', text: this.$t('agenda.statistics.graphs.per_facilities.labels.contacts'), color: this.pallete[0] },
            { key: 'contactsWithLoss', text: this.$t('agenda.statistics.graphs.per_facilities.labels.contactsWithLoss'), color: this.pallete[1] },
            { key: 'leads', text: this.$t('agenda.statistics.graphs.per_facilities.labels.leads'), color: this.pallete[2] },
            { key: 'screenings', text: this.$t('agenda.statistics.graphs.per_promoters.labels.screenings'), color: this.pallete[3] },
          ],
          dataset: this.filteredPromoters || [],
          dataset_key: 'promoter_name',
          criterias: [
            { id: 1, name: this.$t('agenda.statistics.criteria.by_contacts'), criteria: criterias.criteriaByContacts },
            { id: 2, name: this.$t('agenda.statistics.criteria.by_contacts_with_loss'), criteria: criterias.criteriaByContactsWithLoss },
            { id: 3, name: this.$t('agenda.statistics.criteria.by_lqe'), criteria: criterias.criteriaByLeads },
            { id: 4, name: this.$t('agenda.statistics.criteria.by_screenings'), criteria: criterias.criteriaByScreenings },
            { id: 5, name: this.$t('agenda.statistics.criteria.by_contacts_per_screening'), criteria: criterias.criteriaByAverageContactsPerScreening },
            { id: 6, name: this.$t('agenda.statistics.criteria.by_contacts_with_loss_per_screening'), criteria: criterias.criteriaByAverageContactsWithLossPerScreening },
            { id: 7, name: this.$t('agenda.statistics.criteria.by_lqe_per_screening'), criteria: criterias.criteriaByAverageLeadsPerScreening }
          ],
          localStorageKey: 'STATS_TOP_PROMOTERS',
        }
      ]
    },
    filteredPromoters: function () {
      if (this.searchInput.trim()) {
        return this.stats.promoters.filter(item => {
          return item.promoter_name.toLowerCase().includes(this.searchInput.toLowerCase())
        })
      }
      return this.stats.promoters
    },
    filteredFacilities: function () {
      if (this.searchInput.trim()) {
        return this.stats.facilities.filter(item => {
          return item.facility_name.toLowerCase().includes(this.searchInput.toLowerCase())
        })
      }
      return this.stats.facilities
    },
    entityOptions: function () {
      return this.createClustersAndEntitiesList(this.clusters, this.readableEntities)
    },
    readableEntities: function () {
      const permissions = this.component.service.user_permissions.filter(p => p.info_type === 200)
      if (permissions.some(p => p.entity_id === null)) {
        return [...this.component.service.entities]
      } else {
        return this.component.service.entities.filter(entity => permissions.some(p => p.entity_id === entity.id))
      }
    },
    activeEntitiesFilters: function () {
      return this.filters.entities.length ? this.selectedEntityIds.length : this.readableEntities.length
    },
    requestParams: function () {
      return {
        from: this.filters.dateInterval.start,
        until: this.filters.dateInterval.end,
        entities: this.selectedEntityIds.join(',')
      }
    }
  },
  methods: {
    loadStats: function () {
      if(!this.isLoading) {
        this.isLoading = true
        StatisticsService.getAppointmentStatistics(this.requestParams)
          .then(data => {
            let stats = {
              ...data,
              facilities: data.facilities.filter(facility => facility.screenings > 0)
            }
            this.stats = stats
            this.isLoading = false
            this.propsDashboard = {
              contacts: this.stats.num_contacts,
              contactsWithLoss: this.stats.num_contacts_with_loss,
              leads: this.stats.num_leads,
              screenings: this.stats.num_screenings,
              appointments: this.stats.num_appointments,
              promoters: this.stats.promoters.length,
              screenings_cancelled: this.stats.screenings_cancelled
            }
          }).catch((err) => {
          console.log(err)
        })
      }
    },
    loadLocalStorage: async function (state) {

      if (state) {
        // s date filters from local storage
        if (state.from && state.from !== '' && state.until && state.until !== '') {
          this.filters.dateInterval.start = state.from
          this.filters.dateInterval.end = state.until
        }

        if (Array.isArray(state.selectedEntityUids) && state.selectedEntityUids) {
          this.filters.entities = this.reloadSelectedClustersAndEntities(this.entityOptions, state.selectedEntityUids)
        }

        if (state.searchInput && state.searchInput !== '') {
          this.filters.query = state.searchInput
          this.searchInput = state.searchInput
        }
      }
    },
    saveLocalStorage: function () {
      LocalStorageHelper.set(FILTERS_AGENDA_STATISTICS, {
        ...this.requestParams,
        searchInput: this.searchInput,
        selectedEntityUids: this.selectedEntityUids
      })
    }
  },
  watch: {
    'requestParams': {
      handler: function (value) {
        this.saveLocalStorage()
        this.loadStats()
      },
      deep: true
    },
    'filters.entities': function (options) {
      const selectedOptions = this.getSelectedOptionsIdsAndUids(options)
      this.selectedEntityIds = selectedOptions.ids
      this.selectedEntityUids = selectedOptions.uids
    },
    'filters.query': {
      handler: debounce(function (value) {
        this.searchInput = value
        this.saveLocalStorage()
      }, 350)
    }
  }
}
</script>
